import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Zap, Pencil, Plus } from "lucide-react";
import HabitEditor from "@/components/HabitEditor";

export default function Quests() {
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingHabit, setEditingHabit] = useState(null);

  const load = async () => {
    const r = await api.get("/quests/today");
    setData(r.data);
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (habit, e) => {
    // Prevent edit-icon clicks from toggling
    if (e?.target?.closest("[data-role='habit-edit']")) return;
    if (busyKey) return;
    setBusyKey(habit.key);
    const wasCompleted = !!data.completed?.[habit.key];
    try {
      const r = await api.post("/quests/toggle", {
        habit_key: habit.key,
        completed: !wasCompleted,
      });
      if (r.data.leveled_up) {
        toast.success(`⚡ LEVEL UP! You are now LVL ${r.data.new_level}`, { duration: 4000 });
      } else if (!wasCompleted) {
        toast.success(`+${habit.xp} XP · ${habit.name}`);
      } else {
        toast.info(`-${habit.xp} XP · ${habit.name} uncleared`);
      }
      if (r.data.new_achievements?.length) {
        r.data.new_achievements.forEach((a) =>
          toast.success(`🏆 Achievement: ${a.name}`, { duration: 5000 })
        );
      }
      await load();
      await refresh();
    } catch {
      toast.error("Action failed");
    } finally {
      setBusyKey(null);
    }
  };

  const openEdit = (habit) => {
    setEditingHabit(habit);
    setEditorOpen(true);
  };

  const openCreate = () => {
    setEditingHabit(null);
    setEditorOpen(true);
  };

  if (!data) {
    return <div className="label-tech animate-pulse">Loading quest log...</div>;
  }

  const completedCount = Object.keys(data.completed || {}).length;

  return (
    <div className="space-y-6" data-testid="quests-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tech">Daily Quest Log</div>
          <h1 className="h-display text-3xl sm:text-4xl mt-1">
            <span className="text-cyan-400">{completedCount}</span>
            <span className="text-slate-500"> / {data.habits.length}</span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            {data.day} · {data.xp_earned} XP earned today
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="add-quest-btn"
            onClick={openCreate}
            className="btn-neon"
          >
            <Plus size={16} className="inline mr-1" />
            Forge Quest
          </button>
        </div>
      </div>

      <p className="text-xs text-red-400/80 font-mono-tech uppercase tracking-widest">
        System: Skipped quests cost XP. Every clear grants attribute points.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.habits.map((h) => {
          const done = !!data.completed?.[h.key];
          return (
            <div
              key={h.key}
              data-testid={`quest-card-${h.key}`}
              className={`quest-card ${done ? "completed" : ""} relative flex items-center gap-4 p-4`}
            >
              <button
                data-testid={`quest-${h.key}`}
                onClick={(e) => toggle(h, e)}
                disabled={busyKey === h.key}
                className="flex items-center gap-4 flex-1 text-left min-w-0"
              >
                <div
                  className={`w-10 h-10 flex items-center justify-center border shrink-0 ${
                    done
                      ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-300"
                      : "border-white/15 text-slate-400"
                  }`}
                >
                  {done ? <Check size={18} /> : <Zap size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display uppercase tracking-wider text-sm font-semibold truncate">
                    {h.name}
                    {h.custom && (
                      <span className="ml-2 text-[0.55rem] text-violet-400 border border-violet-400/40 px-1.5 py-0.5 font-mono-tech">
                        CUSTOM
                      </span>
                    )}
                  </div>
                  <div className="text-[0.65rem] text-slate-500 font-mono-tech uppercase">
                    +{h.xp} XP · {h.stat}
                  </div>
                </div>
                {done && (
                  <span className="text-[0.6rem] font-mono-tech text-emerald-400 uppercase tracking-widest hidden sm:inline">
                    Cleared
                  </span>
                )}
              </button>

              <button
                data-testid={`quest-edit-${h.key}`}
                data-role="habit-edit"
                onClick={() => openEdit(h)}
                className="shrink-0 p-2 border border-white/10 text-slate-400 hover:border-cyan-400/60 hover:text-cyan-300 transition"
                title="Edit quest"
              >
                <Pencil size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <HabitEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        habit={editingHabit}
        onSaved={load}
      />
    </div>
  );
}
